import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    return { user: null, loading: false, isLoggedIn: false, login: () => {}, logout: () => {} };
  }
  return context;
};

const API_BASE_URL = 'https://pxq7fyhjsf.execute-api.eu-west-1.amazonaws.com/prod';

/**
 * Try to refresh expired tokens using the stored refresh token.
 * Returns true if refresh succeeded, false otherwise.
 */
export async function tryRefreshToken() {
  const refreshToken = localStorage.getItem('transit-calendar-refresh-token');
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (data.idToken) {
      localStorage.setItem('transit-calendar-token', data.idToken);
      if (data.accessToken) localStorage.setItem('transit-calendar-access-token', data.accessToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Checks localStorage for Cognito auth tokens set by TransitCalendarLogin.
 * Returns user data if a valid session exists, null otherwise.
 */
function loadSessionFromStorage() {
  const token = localStorage.getItem('transit-calendar-token');
  if (!token) return null;

  // Check token expiry (JWT payload is base64-encoded middle segment)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      // Token expired — don't clear yet, let async refresh handle it
      return { _expired: true };
    }
  } catch {
    // If we can't parse the token, still try to use the stored user data
  }

  // Try to get user data stored by TransitCalendarLogin
  const storedUser = localStorage.getItem('transit-calendar-user');
  if (storedUser) {
    try {
      const userData = JSON.parse(storedUser);
      return {
        // The login lambda stores the DynamoDB item, whose key is user_id (the Cognito sub).
        id: userData.user_id || userData.userId || userData.id || userData.email,
        userId: userData.user_id || userData.userId || userData.id,
        email: userData.email,
        name: userData.name || userData.displayName || userData.email?.split('@')[0],
        displayName: userData.displayName || userData.name || userData.email?.split('@')[0],
        ...userData,
      };
    } catch {
      // Invalid JSON
    }
  }

  // We have a token but no user data - extract from JWT
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub || payload.email,
      userId: payload.sub,
      email: payload.email,
      name: payload.name || payload.email?.split('@')[0],
      displayName: payload.name || payload.email?.split('@')[0],
    };
  } catch {
    return null;
  }
}

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing Cognito session from TransitCalendarLogin
    const sessionUser = loadSessionFromStorage();
    if (sessionUser && sessionUser._expired) {
      // Token expired — try to refresh in background
      tryRefreshToken().then((refreshed) => {
        if (refreshed) {
          const freshUser = loadSessionFromStorage();
          if (freshUser && !freshUser._expired) {
            setUser(freshUser);
          }
        } else {
          // Refresh failed — clear everything
          localStorage.removeItem('transit-calendar-token');
          localStorage.removeItem('transit-calendar-access-token');
          localStorage.removeItem('transit-calendar-refresh-token');
          localStorage.removeItem('transit-calendar-user');
        }
        setLoading(false);
      });
      // Show stored user data while refreshing
      const storedUser = localStorage.getItem('transit-calendar-user');
      if (storedUser) {
        try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
      }
      return;
    }
    if (sessionUser) {
      setUser(sessionUser);
    } else {
      // Also check the legacy 'user' key
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch { /* ignore */ }
      }
    }
    setLoading(false);
  }, []);

  // Listen for storage changes (e.g. login in another tab or after redirect)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'transit-calendar-token' || e.key === 'transit-calendar-user') {
        const sessionUser = loadSessionFromStorage();
        setUser(sessionUser);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    // Clear all auth tokens
    localStorage.removeItem('user');
    localStorage.removeItem('transit-calendar-token');
    localStorage.removeItem('transit-calendar-access-token');
    localStorage.removeItem('transit-calendar-refresh-token');
    localStorage.removeItem('transit-calendar-user');
  }, []);

  const value = {
    user,
    isLoggedIn: !!user,
    login,
    logout,
    loading,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
